import { ApiFilled } from "@ant-design/icons";
import { IncidentIoIconUrl } from "@p0security/shared/integrations/constants";
import { Alert, Button, Form, Input, Spin, Typography } from "antd";
import { useCallback, useContext, useState } from "react";

import * as firestoreProvider from "../../../providers/FirestoreProvider";
import { useAuthFetch, useHasRole } from "../../Login/hook";
import { Tenant } from "../../Login/util";
import { IntegrationCard } from "../IntegrationCard";
import { OwnerAlert } from "../OwnerAlert";

type FormValues = { apiKey: string };

const InstallForm: React.FC<{
  onInstall: (apiKey: string) => Promise<void>;
  isInstalling: boolean;
}> = ({ onInstall, isInstalling }) => {
  const [form] = Form.useForm<FormValues>();
  const handleSubmit = useCallback(
    async (values: FormValues) => await onInstall(values.apiKey),
    [onInstall]
  );
  return (
    <Form form={form} onFinish={handleSubmit} layout="vertical">
      <Form.Item
        label="API key"
        name="apiKey"
        rules={[
          { required: true, message: "An Incident.io API key is required" },
        ]}
      >
        <Input.Password placeholder="Enter Incident.io API key" />
      </Form.Item>
      <Form.Item>
        <Button
          type="primary"
          htmlType="submit"
          loading={isInstalling}
          icon={<ApiFilled />}
        >
          Install integration
        </Button>
      </Form.Item>
    </Form>
  );
};

const InstallPrompt: React.FC<{ onShowForm: () => void }> = ({
  onShowForm,
}) => (
  <>
    <Typography.Paragraph>
      The API Key requires the following permissions:
    </Typography.Paragraph>
    <ul>
      <li>View data, like public incidents and organization settings</li>
      <li>Create incidents</li>
      <li>Edit incidents</li>
      <li>Manage organization settings</li>
      <li>View catalog types and entries</li>
      <li>Read schedules</li>
    </ul>
    <Typography.Paragraph>
      Instructions for creating an Incident.io API key can be found{" "}
      <a href="https://docs.incident.io/integrations/api-overview#where-can-i-find-the-api-keys">
        here.
      </a>
    </Typography.Paragraph>
    <Button icon={<ApiFilled />} onClick={onShowForm} type="primary">
      Install integration
    </Button>
  </>
);

export const IncidentIo: React.FC<object> = () => {
  const tenantId = useContext(Tenant);
  const integrationData = firestoreProvider.useFirestoreDoc<any>(
    `o/${tenantId}/integrations/incidentio`,
    { live: true }
  );
  const [isInstalling, setIsInstalling] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [showForm, setShowForm] = useState(false);
  const authFetch = useAuthFetch(setError);
  const canInstall = useHasRole("owner");
  const handleShowForm = useCallback(() => setShowForm(true), []);

  const handleInstall = useCallback(
    async (apiKey: string) => {
      setIsInstalling(true);
      setError(undefined);
      const response = await authFetch("integrations/incidentio/config", {
        method: "POST",
        json: { root: { _: { apiKey: { clearText: apiKey } } } },
      });
      setIsInstalling(false);
      if (response) setShowForm(false);
    },
    [authFetch]
  );

  if (integrationData.loading) return <Spin />;

  const isInstalled =
    integrationData.doc?.data?.root?.["_"]?.state === "installed";

  return (
    <IntegrationCard
      integration="incidentio"
      logo={IncidentIoIconUrl}
      title="Incident.io"
      integrationDoc={integrationData.doc}
    >
      {error && (
        <Alert type="error" message={error} style={{ marginBottom: 16 }} />
      )}
      {isInstalled ? (
        <Typography.Text>
          This integration has been installed and is ready for use.
        </Typography.Text>
      ) : showForm ? (
        <InstallForm onInstall={handleInstall} isInstalling={isInstalling} />
      ) : canInstall ? (
        <InstallPrompt onShowForm={handleShowForm} />
      ) : (
        <OwnerAlert />
      )}
    </IntegrationCard>
  );
};
